import { useState } from 'react';
import { IconChevronRight } from '@tabler/icons-react';
import {
  Box,
  Collapse,
  Group,
  Text,
  ThemeIcon,
  Tooltip,
  UnstyledButton,
  useComputedColorScheme,
  Badge,
} from '@mantine/core';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import classes from './Navigation.module.css';
import { useUiStore } from '../../store/uiStore';

interface LinksGroupProps {
  icon?: React.ComponentType<{ size?: number; color?: string }>;
  label: string;
  initiallyOpened?: boolean;
  links?: { label: string; link: string; badge?: number; comingSoon?: boolean }[];
  path?: string;
  excludePaths?: string[];
  badge?: number;
  comingSoon?: boolean;
  /** Пре-релиз: пункт кликабелен, рядом мелкая подпись «beta». */
  beta?: boolean;
  /** Icon-only rail mode: render just the icon with a tooltip. */
  collapsed?: boolean;
}

export function LinksGroup({
  icon: Icon,
  label,
  initiallyOpened,
  links,
  path,
  excludePaths,
  badge,
  comingSoon,
  beta,
  collapsed,
}: LinksGroupProps) {
  const location = useLocation();
  const { t } = useTranslation();
  const setSidebarCollapsed = useUiStore((s) => s.setSidebarCollapsed);
  const hasLinks = Array.isArray(links);
  const [opened, setOpened] = useState(initiallyOpened || false);

  // Match `target` as either an exact pathname or as a parent of a deeper route
  // (e.g. /performance-review/admin should highlight on /performance-review/admin/<id>).
  // The `/` boundary avoids false matches like /admin-foo vs /admin.
  const matchesPath = (target?: string) => {
    if (!target) return false;
    return location.pathname === target || location.pathname.startsWith(target + '/');
  };

  // Top-level path may delegate a subtree to a more-specific nav entry —
  // honour the exclusion list so the parent doesn't compete for highlight.
  const isExcluded = (excludePaths || []).some(
    (p) => location.pathname === p || location.pathname.startsWith(p + '/'),
  );
  const isActive = !isExcluded && matchesPath(path);
  // useComputedColorScheme resolves the 'auto' setting to the actual rendered
  // scheme. useMantineColorScheme would return 'auto', making isDark false even
  // in a dark OS — which painted the active item with a near-white background.
  const computedColorScheme = useComputedColorScheme('light');
  const isDark = computedColorScheme === 'dark';

  // Пункт без подменю — настоящая ссылка (новая вкладка, клавиатура).
  const linkTarget = !hasLinks && path && !comingSoon ? path : undefined;

  // Пункты с путём — ссылки (linkTarget); кнопкой остаются группа и «скоро».
  const handleClick = () => {
    if (!comingSoon && hasLinks) setOpened((o) => !o);
  };

  // Among siblings only the most specific match is active: on
  // /quarterly-scoring/matrix both /quarterly-scoring and the matrix link match,
  // and highlighting both makes it unclear where you are.
  const activeLink = (hasLinks ? links : [])
    .filter((l) => matchesPath(l.link))
    .reduce<
      string | undefined
    >((best, l) => (!best || l.link.length > best.length ? l.link : best), undefined);

  const items = (hasLinks ? links : []).map((link) => (
    <Group
      key={link.label}
      justify="space-between"
      gap="xs"
      style={{ width: '100%', opacity: link.comingSoon ? 0.6 : 1 }}
      pr={{ base: 'md', sm: 0 }}
    >
      {/* Настоящая ссылка: открывается в новой вкладке и доступна с клавиатуры.
          Пункт «скоро» ссылкой не делаем — вести некуда. */}
      <Text
        {...(link.comingSoon
          ? { component: 'span', 'aria-disabled': true }
          : { renderRoot: (props) => <Link to={link.link} {...props} /> })}
        className={classes.link}
        style={{
          backgroundColor:
            link.link === activeLink
              ? isDark
                ? 'var(--mantine-color-dark-6)'
                : 'var(--mantine-color-gray-0)'
              : 'transparent',
          color: link.link === activeLink ? 'var(--mantine-color-teal-7)' : undefined,
          fontWeight: link.link === activeLink ? 600 : 500,
          cursor: link.comingSoon ? 'not-allowed' : 'pointer',
          flex: 1,
          transition: 'background-color 0.2s, color 0.2s',
        }}
      >
        {link.label}
      </Text>
      <Group gap="xs">
        {!!link.badge && link.badge > 0 && (
          <Badge size="sm" color="red" variant="light">
            {link.badge}
          </Badge>
        )}
        {link.comingSoon && (
          <Badge size="xs" color="gray" variant="light">
            {t('common.comingSoon')}
          </Badge>
        )}
      </Group>
    </Group>
  ));

  // Icon-only rail: render just the icon with a tooltip. Items with a path
  // are links; groups expand the sidebar so their submenu becomes usable.
  if (collapsed) {
    const active = isActive || (hasLinks && (links || []).some((l) => matchesPath(l.link)));
    const onCompactClick = () => {
      if (comingSoon || !hasLinks) return;
      setSidebarCollapsed(false);
      setOpened(true);
    };
    return (
      <Tooltip label={label} position="right" withArrow>
        <UnstyledButton
          {...(linkTarget
            ? { renderRoot: (props) => <Link to={linkTarget} {...props} /> }
            : { onClick: onCompactClick, 'aria-disabled': comingSoon || undefined })}
          aria-label={label}
          style={{
            position: 'relative',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            padding: '6px 0',
            borderRadius: 8,
            backgroundColor: active
              ? isDark
                ? 'var(--mantine-color-dark-6)'
                : 'var(--mantine-color-gray-0)'
              : 'transparent',
            cursor: comingSoon ? 'not-allowed' : 'pointer',
            opacity: comingSoon ? 0.6 : 1,
            transition: 'background-color 0.2s',
          }}
        >
          {Icon && (
            <ThemeIcon variant="light" size={34} color="teal">
              <Icon size={20} />
            </ThemeIcon>
          )}
          {badge && badge > 0 ? (
            <Badge
              size="xs"
              color="red"
              variant="filled"
              style={{ position: 'absolute', top: 2, right: 8, pointerEvents: 'none' }}
            >
              {badge}
            </Badge>
          ) : null}
        </UnstyledButton>
      </Tooltip>
    );
  }

  return (
    <>
      <UnstyledButton
        {...(linkTarget
          ? { renderRoot: (props) => <Link to={linkTarget} {...props} /> }
          : { onClick: handleClick, 'aria-disabled': comingSoon || undefined })}
        className={classes.control}
        style={{
          backgroundColor: isActive
            ? isDark
              ? 'var(--mantine-color-dark-6)'
              : 'var(--mantine-color-gray-0)'
            : 'transparent',
          color: isActive ? 'var(--mantine-color-teal-7)' : undefined,
          fontWeight: isActive ? 600 : 500,
          transition: 'background-color 0.2s, color 0.2s',
          cursor: comingSoon ? 'not-allowed' : 'pointer',
          opacity: comingSoon ? 0.6 : 1,
        }}
      >
        <Group justify="space-between" gap={0}>
          <Box style={{ display: 'flex', alignItems: 'center' }}>
            {Icon && (
              <ThemeIcon variant="light" size={30} color="teal">
                <Icon size={18} />
              </ThemeIcon>
            )}
            <Box ml={Icon ? 'md' : 0}>{label}</Box>
          </Box>
          <Group gap="xs">
            {badge && badge > 0 && (
              <Badge size="sm" color="red" variant="filled">
                {badge}
              </Badge>
            )}
            {comingSoon && (
              <Badge size="xs" color="gray" variant="light">
                {t('common.comingSoon')}
              </Badge>
            )}
            {beta && (
              <Badge size="xs" variant="outline">
                {t('common.beta')}
              </Badge>
            )}
            {/* У раздела «скоро» подпункты не раскрываются, поэтому и стрелки нет:
                иначе она обещает клик, которого не будет. */}
            {hasLinks && !comingSoon && (
              <IconChevronRight
                className={classes.chevron}
                stroke={1.5}
                size={16}
                style={{ transform: opened ? 'rotate(-90deg)' : 'none' }}
              />
            )}
          </Group>
        </Group>
      </UnstyledButton>
      {hasLinks ? (
        <Collapse style={{ width: '100%' }} in={opened}>
          {items}
        </Collapse>
      ) : null}
    </>
  );
}
